/**
 * Título, description e indexação de cada rota.
 *
 * Uma lista só para o `<head>` (`components/Metadados`) e para o sitemap
 * (`scripts/gerarSitemap.js`): página que entra no índice do Google entra no
 * sitemap, e nenhuma entra em um sem o outro.
 *
 * Dados puros e sem import, de propósito: o script do sitemap lê este arquivo
 * fora do webpack, pelo mesmo truque de `scripts/verificarScore.js`.
 *
 * `titulo` e `descricao` são chaves do dicionário. Rota com `indexar` ganha
 * canonical e description; qualquer outra — área logada, fluxos de conta,
 * 404 — leva `noindex`, e caminho fora da lista é tratado como 404. Os
 * parâmetros do padrão (`:id`) chegam ao `t()`, então o nome aqui é o da
 * variável no texto, não o da rota em `App.js`.
 */

export const SITE_URL = 'https://silentpuma.com';

/**
 * Onde mora cada idioma das páginas públicas. Inglês fica na raiz: são as URLs
 * que o Google já indexou, e o host estático não redireciona por idioma.
 */
export const PREFIXOS = { en: '', pt: '/pt-br', es: '/es' };

/** Como o `hreflang` e o `<html lang>` chamam cada idioma. */
export const HREFLANG = { en: 'en', pt: 'pt-BR', es: 'es' };

/** `/plans` em português é `/pt-br/plans`; a home é `/pt-br`, sem barra no fim. */
export const comPrefixo = (idioma, caminho) =>
  PREFIXOS[idioma] ? PREFIXOS[idioma] + (caminho === '/' ? '' : caminho) : caminho;

/** Separa `/pt-br/plans` em `pt` e `/plans`. Sem prefixo, `idioma` é `null`. */
export const lerPrefixo = (pathname) => {
  for (const [idioma, prefixo] of Object.entries(PREFIXOS)) {
    if (prefixo && (pathname === prefixo || pathname.startsWith(`${prefixo}/`))) {
      return { idioma, caminho: pathname.slice(prefixo.length) || '/' };
    }
  }
  return { idioma: null, caminho: pathname };
};

/** O título como sai na aba e no Google: com a marca, se ainda não a tiver. */
export const tituloCompleto = (nome) =>
  nome.includes('Silent Puma') ? nome : `${nome} — Silent Puma`;

export const PAGINAS = [
  { caminho: '/', titulo: 'seo.homeTitulo', descricao: 'seo.homeDescricao', indexar: true },
  { caminho: '/blog', titulo: 'seo.blogTitulo', descricao: 'seo.blogDescricao', indexar: true },
  {
    caminho: '/plans',
    titulo: 'seo.planosTitulo',
    descricao: 'seo.planosDescricao',
    indexar: true
  },
  {
    caminho: '/privacy-policy',
    titulo: 'seo.privacidadeTitulo',
    descricao: 'seo.privacidadeDescricao',
    indexar: true
  },
  {
    caminho: '/terms-of-service',
    titulo: 'seo.termosTitulo',
    descricao: 'seo.termosDescricao',
    indexar: true
  },
  {
    caminho: '/login',
    titulo: 'seo.entrarTitulo',
    descricao: 'seo.entrarDescricao',
    indexar: true
  },
  {
    caminho: '/signup',
    titulo: 'seo.cadastroTitulo',
    descricao: 'seo.cadastroDescricao',
    indexar: true
  },

  { caminho: '/forgot-password', titulo: 'acesso.esqueciTitulo' },
  { caminho: '/reset-password/:token', titulo: 'acesso.novaSenhaTitulo' },
  { caminho: '/convite/:token', titulo: 'equipe.conviteTitulo' },
  { caminho: '/complete-signup', titulo: 'acesso.concluirTitulo' },
  { caminho: '/auth/callback', titulo: 'acesso.entrar' },
  { caminho: '/user-home', titulo: 'menu.painel' },
  { caminho: '/projects', titulo: 'projeto.titulo' },
  { caminho: '/create-project', titulo: 'criar.titulo' },
  { caminho: '/settings', titulo: 'conta.titulo' },
  { caminho: '/payments', titulo: 'pagamentos.titulo' },
  { caminho: '/docs', titulo: 'docs.titulo' },
  { caminho: '/crawl-config/:projectId', titulo: 'crawl.titulo' },
  { caminho: '/site-dashboard/:projectId', titulo: 'painelSite.titulo' },
  { caminho: '/youtube-dashboard/:projectId', titulo: 'painelYt.titulo' },
  { caminho: '/project/:projectId/runs', titulo: 'menu.projetos' },
  { caminho: '/project/:projectId/report', titulo: 'relatorio.titulo' },
  { caminho: '/project/:projectId/integrations', titulo: 'integracoes.titulo' },
  { caminho: '/optimizations/:runId', titulo: 'otim.titulo' },
  { caminho: '/run/:id', titulo: 'runAnalise.titulo' }
];
